// useIsMobile — single source of truth for the mobile breakpoint.
//
// Previously each surface carried its own copy (Workspace.tsx, CaseAIRail.tsx);
// they agreed on 768px but drift was only a matter of time. This is the shared
// hook every responsive layout decision should route through.
//
// 768px matches the CSS side of the same line: `responsive.css`'s
// `@media (max-width: 768px)` shims and Tailwind's `md:` breakpoint. Keep all
// three in lockstep — a JS/CSS breakpoint mismatch produces a dead zone where
// the layout is neither the mobile nor the desktop shape.

import { useEffect, useState } from "react";

export const MOBILE_BREAKPOINT = 768;

export function useIsMobile(breakpoint: number = MOBILE_BREAKPOINT): boolean {
  // SSR-safe init: `window` is undefined during server render / tests.
  const [isMobile, setIsMobile] = useState<boolean>(
    () => typeof window !== "undefined" && window.innerWidth < breakpoint,
  );

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < breakpoint);
    onResize(); // sync once in case the viewport changed before mount
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpoint]);

  return isMobile;
}
